import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp, Globe, Search, Clock, Ticket, Train, Calendar, TreePine, Star, Accessibility, UtensilsCrossed, Backpack } from 'lucide-react';
import { DirectAnswer } from '../components/AEOComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Language configurations
const LANGUAGES = {
  fr: { name: 'Français', flag: '🇫🇷', title: 'Questions fréquentes sur Versailles', subtitle: 'Tout ce que vous devez savoir pour préparer votre visite' },
  en: { name: 'English', flag: '🇬🇧', title: 'Versailles FAQ', subtitle: 'Everything you need to know to plan your visit' },
  es: { name: 'Español', flag: '🇪🇸', title: 'Preguntas frecuentes sobre Versalles', subtitle: 'Todo lo que necesitas saber para planificar tu visita' },
  de: { name: 'Deutsch', flag: '🇩🇪', title: 'Häufige Fragen zu Versailles', subtitle: 'Alles, was Sie für Ihren Besuch wissen müssen' },
  it: { name: 'Italiano', flag: '🇮🇹', title: 'Domande frequenti su Versailles', subtitle: 'Tutto quello che devi sapere per pianificare la tua visita' },
  ja: { name: '日本語', flag: '🇯🇵', title: 'ヴェルサイユよくある質問', subtitle: '訪問の計画に必要な情報' },
  zh: { name: '中文', flag: '🇨🇳', title: '凡尔赛常见问题', subtitle: '规划您的访问所需了解的一切' }
};

// Icons for each FAQ category/topic
const FAQ_ICONS = {
  'acheter-billets': Ticket,
  'buy-tickets': Ticket,
  'temps-visite': Clock,
  'how-long': Clock,
  'aller-versailles': Train,
  'how-to-get': Train,
  'horaires': Calendar,
  'opening-hours': Calendar,
  'reservation': Calendar,
  'book-advance': Calendar,
  'jardins-gratuit': TreePine,
  'gardens-free': TreePine,
  'que-voir': Star,
  'what-to-see': Star,
  'accessibilite': Accessibility,
  'wheelchair': Accessibility,
  'manger': UtensilsCrossed,
  'eat': UtensilsCrossed,
  'sac-a-dos': Backpack,
  'backpack': Backpack
};

const getIconForFaq = (slug) => {
  for (const [key, Icon] of Object.entries(FAQ_ICONS)) {
    if (slug.includes(key)) return Icon;
  }
  return Search;
};

// Single FAQ Item Component
const FAQItem = ({ faq, isOpen, onToggle, index }) => {
  const Icon = getIconForFaq(faq.slug);
  
  return (
    <div 
      className="faq-item bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden mb-4 transition-all hover:shadow-md"
      itemScope
      itemType="https://schema.org/Question"
    >
      <button
        className="w-full px-6 py-5 flex items-center gap-4 text-left hover:bg-stone-50 transition-colors"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${index}`}
      >
        <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
          <Icon className="w-6 h-6 text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 
            className="text-lg font-semibold text-stone-900"
            itemProp="name"
          >
            {faq.question}
          </h3>
          {faq.short_answer && !isOpen && (
            <p className="text-sm text-stone-500 mt-1 line-clamp-1">{faq.short_answer}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-stone-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-stone-400" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div 
          className="px-6 pb-6"
          itemScope
          itemType="https://schema.org/Answer"
          itemProp="acceptedAnswer"
        >
          {/* Direct Answer Block */}
          {faq.direct_answer && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-4">
              <p className="text-stone-800 font-medium" itemProp="text">
                {faq.direct_answer}
              </p>
            </div>
          )}
          
          {/* Full Answer with Markdown */}
          {faq.answer && (
            <div 
              className="prose prose-stone max-w-none mt-4 faq-content"
              dangerouslySetInnerHTML={{ 
                __html: faq.answer
                  .replace(/^### (.*$)/gim, '<h4 class="text-lg font-semibold text-stone-800 mt-4 mb-2">$1</h4>')
                  .replace(/^## (.*$)/gim, '<h3 class="text-xl font-bold text-stone-900 mt-5 mb-3">$1</h3>')
                  .replace(/^\*\*(.*?)\*\*/gm, '<strong>$1</strong>')
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
                  .replace(/^• (.*$)/gim, '<li class="ml-4">$1</li>')
                  .replace(/✅/g, '<span class="text-green-600">✅</span>')
                  .replace(/❌/g, '<span class="text-red-600">❌</span>')
                  .replace(/⚠️/g, '<span class="text-amber-600">⚠️</span>')
                  .replace(/\n\n/g, '</p><p class="mb-3">')
                  .replace(/\n/g, '<br/>')
                  .replace(/\|(.+)\|/g, (match) => {
                    // Basic table support
                    return `<div class="overflow-x-auto my-3"><table class="min-w-full border-collapse">${match}</table></div>`;
                  })
              }}
            />
          )}
          
          {/* Practical Info if available */}
          {faq.practical_info && Object.keys(faq.practical_info).length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <h4 className="text-sm font-semibold text-stone-700 mb-2">📋 En bref</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(faq.practical_info).slice(0, 6).map(([key, value]) => (
                  <div key={key} className="bg-stone-100 rounded-lg px-3 py-2">
                    <span className="text-xs text-stone-500 capitalize">{key.replace(/_/g, ' ')}</span>
                    <p className="text-sm font-medium text-stone-800">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Language Selector Component
const LanguageSelector = ({ currentLang, onChangeLang }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-stone-500" />
        <span className="text-lg">{LANGUAGES[currentLang]?.flag}</span>
        <span className="text-sm font-medium">{LANGUAGES[currentLang]?.name}</span>
        <ChevronDown className="w-4 h-4 text-stone-400" />
      </button>
      
      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white border border-stone-200 rounded-lg shadow-lg z-50 min-w-[180px]">
          {Object.entries(LANGUAGES).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => {
                onChangeLang(code);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-stone-50 transition-colors ${
                code === currentLang ? 'bg-amber-50' : ''
              }`}
              data-testid={`lang-${code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Main FAQ Page Component
const FAQPage = () => {
  const { lang: urlLang } = useParams();
  const navigate = useNavigate();
  
  // Determine language from URL path
  const getLanguageFromPath = () => {
    const path = window.location.pathname;
    for (const lang of Object.keys(LANGUAGES)) {
      if (path.startsWith(`/${lang}/`)) {
        return lang;
      }
    }
    return 'fr'; // Default to French
  };
  
  const [currentLang, setCurrentLang] = useState(urlLang || getLanguageFromPath());
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(0); // First FAQ open by default
  const [error, setError] = useState(null);
  
  // Update lang when URL changes
  useEffect(() => {
    if (urlLang && LANGUAGES[urlLang]) {
      setCurrentLang(urlLang);
    } else {
      setCurrentLang(getLanguageFromPath());
    }
  }, [urlLang]);
  
  // Fetch FAQs
  useEffect(() => {
    const fetchFAQs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/api/geo-faq?lang=${currentLang}&limit=50`);
        if (!response.ok) throw new Error('Failed to fetch FAQs');
        const data = await response.json();
        
        // Sort: TOP 10 first, then by category
        const sortedFaqs = (data.geo_faq || []).sort((a, b) => {
          if (a.is_top10 && !b.is_top10) return -1;
          if (!a.is_top10 && b.is_top10) return 1;
          return 0;
        });
        
        // Only show TOP 10 for this page
        const top10 = sortedFaqs.filter(f => f.is_top10);
        setFaqs(top10.length > 0 ? top10 : sortedFaqs.slice(0, 10));
      } catch (err) {
        console.error('Error fetching FAQs:', err);
        setError('Unable to load FAQs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFAQs();
  }, [currentLang]);
  
  // Handle language change
  const handleLangChange = (newLang) => {
    setCurrentLang(newLang);
    if (newLang === 'fr') {
      navigate('/faq');
    } else {
      navigate(`/${newLang}/faq`);
    }
  };
  
  // Ensure langConfig is always defined
  // Ensure langConfig is always defined
  const langConfig = LANGUAGES[currentLang] || LANGUAGES.fr;
  const pageTitle = langConfig.title || 'Questions fréquentes';
  const pageSubtitle = langConfig.subtitle || 'Tout ce que vous devez savoir pour préparer votre visite';
  
  // Generate FAQ Schema only when FAQs are loaded
  const faqSchemaString = faqs.length > 0 ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question || "",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.direct_answer || faq.short_answer || (faq.answer ? faq.answer.substring(0, 500) : "")
      }
    }))
  }) : null;
  
  // Canonical URL
  const canonicalUrl = `https://www.versaillesplus.fr${currentLang === 'fr' ? '/faq' : `/${currentLang}/faq`}`;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      <Helmet>
        <title>{`${pageTitle} | VersaillesPlus`}</title>
        <meta name="description" content={pageSubtitle} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Alternate language links for SEO */}
        <link rel="alternate" hrefLang="fr" href="https://www.versaillesplus.fr/faq" />
        <link rel="alternate" hrefLang="en" href="https://www.versaillesplus.fr/en/faq" />
        <link rel="alternate" hrefLang="es" href="https://www.versaillesplus.fr/es/faq" />
        <link rel="alternate" hrefLang="de" href="https://www.versaillesplus.fr/de/faq" />
        <link rel="alternate" hrefLang="it" href="https://www.versaillesplus.fr/it/faq" />
        <link rel="alternate" hrefLang="ja" href="https://www.versaillesplus.fr/ja/faq" />
        <link rel="alternate" hrefLang="zh" href="https://www.versaillesplus.fr/zh/faq" />
        <link rel="alternate" hrefLang="x-default" href="https://www.versaillesplus.fr/faq" />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${pageTitle} | VersaillesPlus`} />
        <meta property="og:description" content={pageSubtitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={currentLang === 'fr' ? 'fr_FR' : currentLang === 'en' ? 'en_US' : currentLang} />
      </Helmet>
      
      {/* FAQ Schema JSON-LD - Rendered outside Helmet to avoid string issues */}
      {faqSchemaString && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqSchemaString }}
        />
      )}
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex justify-between items-start mb-6">
            <Link to="/" className="text-amber-100 hover:text-white text-sm">
              ← {currentLang === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
            </Link>
            <LanguageSelector currentLang={currentLang} onChangeLang={handleLangChange} />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="faq-title">
            {pageTitle}
          </h1>
          <p className="text-lg text-amber-100">
            {pageSubtitle}
          </p>
          
          {/* Quick stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            <div className="bg-white/20 rounded-lg px-4 py-2">
              <span className="text-2xl font-bold">10</span>
              <span className="text-amber-100 ml-2">{currentLang === 'fr' ? 'questions essentielles' : 'essential questions'}</span>
            </div>
            <div className="bg-white/20 rounded-lg px-4 py-2">
              <span className="text-2xl font-bold">7</span>
              <span className="text-amber-100 ml-2">{currentLang === 'fr' ? 'langues' : 'languages'}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ List */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-stone-200 p-6 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-stone-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-5 bg-stone-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-stone-100 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              {currentLang === 'fr' ? 'Réessayer' : 'Retry'}
            </button>
          </div>
        ) : (
          <>
            {/* First FAQ with DirectAnswer highlighted */}
            {faqs.length > 0 && faqs[0].direct_answer && (
              <DirectAnswer 
                answer={faqs[0].direct_answer}
                question={faqs[0].question}
              />
            )}
            
            {/* FAQ List */}
            <div className="space-y-1" data-testid="faq-list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={faq.id || faq.slug || index}
                  faq={faq}
                  index={index}
                  isOpen={openIndex === index}
                  onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
            
            {/* CTA Section */}
            <div className="mt-12 bg-gradient-to-r from-stone-800 to-stone-900 text-white rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">
                {currentLang === 'fr' ? 'Prêt à visiter Versailles ?' : 'Ready to visit Versailles?'}
              </h2>
              <p className="text-stone-300 mb-6">
                {currentLang === 'fr' 
                  ? 'Découvrez nos guides complets pour préparer votre visite.'
                  : 'Discover our complete guides to plan your visit.'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link 
                  to="/guides"
                  className="px-6 py-3 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors"
                >
                  {currentLang === 'fr' ? 'Voir les guides' : 'View guides'}
                </Link>
                <a 
                  href="https://www.chateauversailles.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
                >
                  {currentLang === 'fr' ? 'Site officiel' : 'Official site'}
                </a>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Related Links */}
      <div className="bg-stone-100 py-10">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-stone-800 mb-4">
            {currentLang === 'fr' ? 'Ressources utiles' : 'Useful resources'}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link 
              to="/guides"
              className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="font-semibold text-stone-900">
                {currentLang === 'fr' ? '📚 Guides de visite' : '📚 Visit guides'}
              </h3>
              <p className="text-sm text-stone-600 mt-1">
                {currentLang === 'fr' ? 'Itinéraires et conseils' : 'Itineraries and tips'}
              </p>
            </Link>
            <Link 
              to="/lieux"
              className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="font-semibold text-stone-900">
                {currentLang === 'fr' ? '📍 Lieux à voir' : '📍 Places to see'}
              </h3>
              <p className="text-sm text-stone-600 mt-1">
                {currentLang === 'fr' ? 'Galerie des Glaces, Trianon...' : 'Hall of Mirrors, Trianon...'}
              </p>
            </Link>
            <Link 
              to="/actualites"
              className="bg-white p-4 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="font-semibold text-stone-900">
                {currentLang === 'fr' ? '📰 Actualités' : '📰 News'}
              </h3>
              <p className="text-sm text-stone-600 mt-1">
                {currentLang === 'fr' ? 'Dernières infos Versailles' : 'Latest Versailles news'}
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
